import { useState } from "react";

import {
  useGenerate,
  useGenerationPreview,
  useGenerationTemplate,
  useTaxonomy,
  type GenerationTemplateOut,
} from "@/api/queries";
import { Button } from "@/components/ui/button";
import { Combobox, FieldLabel, Input, Label, Select, Textarea } from "@/components/ui/input";
import { GeneratedReview } from "@/components/generated-review";
import { Callout, ErrorNote } from "@/components/ui/panel";

const DIFFICULTIES = [
  { value: "low_to_high", label: "Low to High" },
  { value: "medium_to_high", label: "Medium to High" },
  { value: "low_to_medium", label: "Low to Medium" },
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
] as const;

type Difficulty = (typeof DIFFICULTIES)[number]["value"];

const PLACEHOLDER = /\{([A-Za-z_]+)\}/g;

/**
 * Why an edited prompt cannot be sent, or null. The same three rules as
 * `providers/prompts.validate_generation_template`, run on every keystroke so the button
 * says why it is disabled; the API repeats them, because it is the one that has to be right.
 */
function templateProblem(text: string, tpl: GenerationTemplateOut): string | null {
  if (!text.trim()) return "The prompt is empty.";
  if (text.length > tpl.max_chars)
    return `The prompt is ${text.length} characters; the limit is ${tpl.max_chars}.`;
  const used = new Set([...text.matchAll(PLACEHOLDER)].map((m) => m[1]));
  const known = new Set(tpl.placeholders.map((p) => p.name));
  const unknown = [...used].find((name) => !known.has(name ?? ""));
  if (unknown) return `{${unknown}} is not a placeholder this prompt fills in.`;
  const missing = tpl.placeholders.filter((p) => p.required && !used.has(p.name)).map((p) => `{${p.name}}`);
  if (missing.length > 0) return `The prompt must keep ${missing.join(", ")}.`;
  return null;
}

/**
 * The alternative to uploading a document: pick a stack and a count, and the worker asks the
 * model for the questions. They are shown here first (`GeneratedReview`); the ones kept enter the
 * pipeline as `extracted` (§6) and go through enrich → embed → dedup → cluster → review like
 * anything uploaded, and the rest are deleted. The same dedup gate is what guarantees they are
 * new, the exclusion list in the prompt only makes that cheaper.
 *
 * The prompt is editable per run. An untouched editor sends nothing and follows the built-in
 * template, so an improvement to the default reaches every run that never changed it.
 */
export function GeneratePanel() {
  const { data: stacks } = useTaxonomy("stack");
  const { data: tpl } = useGenerationTemplate();
  const generate = useGenerate();

  const [stack, setStack] = useState("");
  const [countText, setCountText] = useState("20");
  const [difficulty, setDifficulty] = useState<Difficulty>("low_to_high");
  const [focus, setFocus] = useState("");
  const [edited, setEdited] = useState<string | null>(null);
  const [showPrompt, setShowPrompt] = useState(false);

  const preview = useGenerationPreview(stack);

  const count = Number.parseInt(countText, 10);
  const maxPerRun = tpl?.max_per_run ?? 100;
  const countValid = Number.isInteger(count) && count >= 1 && count <= maxPerRun;
  const text = edited ?? tpl?.template ?? "";
  const promptProblem = tpl && edited !== null ? templateProblem(edited, tpl) : null;
  const calls = countValid && tpl ? Math.ceil(count / tpl.per_call) : null;

  function start() {
    if (!stack || !countValid || promptProblem) return;
    generate.mutate({
      stack,
      count,
      focus: focus.trim() || null,
      difficulty,
      prompt_template: edited,
    });
  }

  return (
    <div className="space-y-3 p-4">
      <p className="text-bank-muted text-xs">
        No file needed: the model writes new questions for a stack, told which ones the bank already has. You
        pick the ones worth keeping; those go through the same pipeline and review as an uploaded document and
        the rest are deleted.
      </p>

      <GeneratedReview />

      <div>
        <FieldLabel htmlFor="gen-stack">Tech stack (required)</FieldLabel>
        <Combobox
          id="gen-stack"
          value={stack}
          onChange={setStack}
          options={(stacks ?? []).map((s) => ({ value: s.path, label: s.path }))}
        />
        {stack && preview.data && (
          <p className="text-bank-muted mt-1 text-xs">
            {preview.data.existing_total === 0 ? (
              <>Nothing in the bank for this stack yet, so nothing to exclude.</>
            ) : (
              <>
                <strong>{preview.data.existing_total}</strong> existing question
                {preview.data.existing_total === 1 ? "" : "s"} in <code>{preview.data.stack}</code> and below
                will be excluded
                {preview.data.summarised > 0
                  ? ` (${preview.data.listed_in_prompt} listed, ${preview.data.summarised} more summarised by concept)`
                  : ""}
                .
              </>
            )}
            {preview.data.current_version ? <> Current version: {preview.data.current_version}.</> : null}
          </p>
        )}
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <FieldLabel htmlFor="gen-count">How many questions</FieldLabel>
          <Input
            id="gen-count"
            type="number"
            min={1}
            max={maxPerRun}
            value={countText}
            onChange={(e) => setCountText(e.target.value)}
            aria-invalid={!countValid}
          />
          <p className="text-bank-muted mt-1 text-xs">
            {countValid ? (
              <>
                {calls} generation call{calls === 1 ? "" : "s"}, plus what the pipeline spends on them.
              </>
            ) : (
              <>Between 1 and {maxPerRun}.</>
            )}
          </p>
        </div>
        <div>
          <FieldLabel htmlFor="gen-difficulty">Difficulty</FieldLabel>
          <Select
            id="gen-difficulty"
            value={difficulty}
            onChange={(e) => setDifficulty(e.target.value as Difficulty)}
          >
            {DIFFICULTIES.map((d) => (
              <option key={d.value} value={d.value}>
                {d.label}
              </option>
            ))}
          </Select>
        </div>
      </div>

      <div>
        <FieldLabel htmlFor="gen-focus">Focus (optional)</FieldLabel>
        <Input
          id="gen-focus"
          value={focus}
          maxLength={300}
          placeholder="e.g. change detection, dependency injection"
          onChange={(e) => setFocus(e.target.value)}
        />
      </div>

      <div className="border-bank-border rounded-md border">
        <button
          type="button"
          onClick={() => setShowPrompt((v) => !v)}
          aria-expanded={showPrompt}
          className="hover:bg-bank-surface-2 flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm"
        >
          <span className="font-medium">Prompt</span>
          <span className="text-bank-muted text-xs">
            {edited === null ? "built-in template" : "edited for this run"} · {showPrompt ? "hide" : "show"}
          </span>
        </button>
        {showPrompt && tpl && (
          <div className="border-bank-border space-y-2 border-t p-3">
            <Label htmlFor="gen-prompt">
              Edit the prompt for this run only. Placeholders are filled in when the run starts.
            </Label>
            <Textarea
              id="gen-prompt"
              value={text}
              rows={14}
              spellCheck={false}
              onChange={(e) => setEdited(e.target.value === tpl.template ? null : e.target.value)}
              className="font-mono text-xs leading-relaxed"
              aria-invalid={promptProblem !== null}
            />
            <ul className="text-bank-muted space-y-0.5 text-xs">
              {tpl.placeholders.map((p) => (
                <li key={p.name}>
                  <code>{`{${p.name}}`}</code>
                  {p.required ? " (required)" : ""} — {p.description}
                </li>
              ))}
            </ul>
            {promptProblem && <Callout tone="bad">{promptProblem}</Callout>}
            <Button size="sm" disabled={edited === null} onClick={() => setEdited(null)}>
              Reset to built-in template
            </Button>
          </div>
        )}
      </div>

      <ErrorNote error={generate.error} />

      <Button
        variant="primary"
        disabled={!stack || !countValid || promptProblem !== null || generate.isPending}
        onClick={start}
      >
        {generate.isPending ? "Queueing…" : `Generate ${countValid ? count : ""} questions for review`}
      </Button>

      {generate.data && (
        <Callout tone="good">
          Queued job #{generate.data.job_id}. Watch it under <strong>Jobs</strong> below. When it finishes,
          the questions appear above for you to review; nothing enters the bank until you keep some.
        </Callout>
      )}

      <p className="text-bank-muted text-xs">
        This spends model quota (the free tier is 20 requests per day per model, DESIGN §19.5 #7). The model
        call happens in the worker, never in this request (§8 invariant 01); a run that runs out of quota
        pauses and resumes rather than failing.
      </p>
    </div>
  );
}
